import React from "react";
import { createRoot } from "react-dom/client";
import UserRolesTable from "./UserRolesTable";
import Resources from "../types/Resources";
import Department from "../types/Department";
import Role from "../types/Role";
import Departments from "./Departments";
import MultiSelectDropdown, { MultiSelectDropdownProps } from "./Common/MultiSelectDropdown";
import GeneralDepartmentPermitForm, {
    GeneralDepartmentPermitFormProps
} from "./Forms/Permits/GeneralDepartmentPermitForm";
import EnergyHarbourPermitForm, { EnergyHarbourPermitFormProps } from "./Forms/Permits/EnergyHarbourPermitForm";
import CoordinationPermitForm from "./Forms/Permits/CoordinationPermitForm";
import MaritimeEventForm from "./Forms/Permits/MaritimeEventForm";
import ShipRepairsPermitForm from "./Forms/Permits/ShipRepairsPermitForm";
import { AnonymousPermitFormProps, PermitFormProps } from "../types/forms/Permits/PermitFormProps";
import DivingPermitForm from "./Forms/Permits/DivingPermitForm";
import HullCleaningPermitForm from "./Forms/Permits/HullCleaningPermitForm";
import HotWorkOnShipPermitForm from "./Forms/Permits/HotWorkOnShipPermitForm";
import UnderWaterWorkPermitForm from "./Forms/Permits/UnderWaterWorkPermitForm";
import PermitsTable, { PermitsTableProps } from "./PermitsTable";
import CompaniesTable, { CompaniesTableProps } from "./Companies";
import CompanyDepartmentsTable, { CompanyDepartmentsTableProps } from "./CompanyDepartments";
import CreateUserForm, { CreateUserFormProps } from "./UserForm/CreateUserForm";
import EditUserForm, { EditUserFormProps } from "./UserForm/EditUserForm";

function enableComponent(containerId: string, component: JSX.Element) {
    const container = document.getElementById(containerId);

    if (!container) {
        console.error(`No element with id "${containerId}" was found.`);
        return;
    }

    const root = createRoot(container);
    root.render(component);
}

export function enableDepartmentsList(resources: Resources, canCreateDepartments: boolean) {
    enableComponent(
        "departments-list-component",
        <Departments resources={resources} userCanCreateDepartments={canCreateDepartments} />
    );
}

export function enableGeneralDepartmentPermit(props: GeneralDepartmentPermitFormProps) {
    enableComponent("general-department-permit-component", <GeneralDepartmentPermitForm {...props} />);
}

export function enableMultiSelectDropdown(props: MultiSelectDropdownProps) {
    enableComponent("multi-select-dropdown-component", <MultiSelectDropdown {...props} />);
}

export function enableEnergyHarbourPermit(props: EnergyHarbourPermitFormProps) {
    enableComponent("energy-harbour-permit-component", <EnergyHarbourPermitForm {...props} />);
}

export function enableCoordinationPermit(props: PermitFormProps) {
    enableComponent("coordination-permit-component", <CoordinationPermitForm {...props} />);
}

export function enableMaritimeEvent(props: PermitFormProps) {
    enableComponent("maritime-event-component", <MaritimeEventForm {...props} />);
}

export function enableShipRepairsPermit(props: AnonymousPermitFormProps) {
    enableComponent("ship-repairs-permit-component", <ShipRepairsPermitForm {...props} />);
}

export function enableDivingPermit(props: PermitFormProps) {
    enableComponent("diving-permit-component", <DivingPermitForm {...props} />);
}

export function enableHullCleaningPermit(props: AnonymousPermitFormProps) {
    enableComponent("hull-cleaning-permit-component", <HullCleaningPermitForm {...props} />);
}

export function enableHotWorkOnShipPermit(props: AnonymousPermitFormProps) {
    enableComponent("hot-work-on-ship-permit-component", <HotWorkOnShipPermitForm {...props} />);
}

export function enableUnderWaterWorkPermit(props: PermitFormProps) {
    enableComponent("under-water-work-permit-component", <UnderWaterWorkPermitForm {...props} />);
}

export function enablePermitsTable(props: PermitsTableProps) {
    enableComponent("permits-table-component", <PermitsTable {...props} />);
}

export function enableCompaniesTable(props: CompaniesTableProps) {
    enableComponent("companies-table-component", <CompaniesTable {...props} />);    
}

export function enableCompanyDepartmentsTable(props: CompanyDepartmentsTableProps) {
    enableComponent("company-departments-table-component", <CompanyDepartmentsTable {...props} />)
}

export function enableCreateUserForm(props: CreateUserFormProps) {
    enableComponent("create-user-form-component", <CreateUserForm {...props} />);
}

export function enableEditUserForm(props: EditUserFormProps) {
    enableComponent("edit-user-form-component", <EditUserForm {...props} />);
}
    